/**
 * In-memory registry of DSL functions keyed by dotted path
 * (e.g. "Watchlist.Add"). Modules call `register(...)` at import
 * time so adding new functions never touches the parser or engine.
 */

import type {
  DslFunction,
  DslFunctionMeta,
  DslListedFunction,
  DslRegisteredFunction,
} from "./types";

export class DslRegistry {
  private fns = new Map<string, DslRegisteredFunction>();

  register(path: string, fn: DslFunction, meta: DslFunctionMeta): void {
    if (!path) {
      throw new Error("DslRegistry.register: path must be non-empty");
    }
    if (this.fns.has(path)) {
      throw new Error(`DslRegistry.register: duplicate path '${path}'`);
    }
    this.fns.set(path, { fn, meta });
  }

  resolve(path: string): DslRegisteredFunction | undefined {
    return this.fns.get(path);
  }

  has(path: string): boolean {
    return this.fns.has(path);
  }

  list(): DslListedFunction[] {
    return [...this.fns.entries()]
      .map(([path, entry]) => ({ path, ...entry.meta }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }

  size(): number {
    return this.fns.size;
  }
}

/**
 * Shared registry instance used by both modules (at import time) and
 * the engine (at execute time). A single module graph means a single
 * instance — keeping this top-level avoids passing it through every
 * module barrel.
 */
export const dslRegistry = new DslRegistry();
